import { supabase } from './supabase'
import { getServicePrice } from './appointmentUtils'

const TIME_RANGES = {
	weekly: 7,
	monthly: 30,
	yearly: 365
}

/**
 * Resolve a range into { start, end } ISO strings.
 * Accepts preset keywords, 'yyyy-mm-dd' strings, or { from, to } objects.
 * @param {string|{from: string, to: string}} range
 * @returns {{ start: string, end: string }}
 */
export function resolveRange(range) {
	if (typeof range === 'object' && range.from && range.to) {
		return {
			start: new Date(range.from + (range.from.includes('T') ? '' : 'T00:00:00')).toISOString(),
			end: new Date(range.to + (range.to.includes('T') ? '' : 'T23:59:59')).toISOString()
		}
	}
	if (TIME_RANGES[range]) {
		const d = new Date()
		d.setDate(d.getDate() - TIME_RANGES[range])
		return { start: d.toISOString(), end: new Date().toISOString() }
	}
	return {
		start: new Date(range + 'T00:00:00').toISOString(),
		end: new Date().toISOString()
	}
}

// /**
//  * Resolve a future-looking range into { start, end } ISO strings.
//  */
// function resolveFutureRange(range) {
// 	if (typeof range === 'object' && range.from && range.to) {
// 		return {
// 			start: new Date().toISOString(),
// 			end: new Date(range.to + (range.to.includes('T') ? '' : 'T23:59:59')).toISOString()
// 		}
// 	}
// 	if (TIME_RANGES[range]) {
// 		const d = new Date()
// 		d.setDate(d.getDate() + TIME_RANGES[range])
// 		return { start: new Date().toISOString(), end: d.toISOString() }
// 	}
// 	return {
// 		start: new Date().toISOString(),
// 		end: new Date(range + 'T23:59:59').toISOString()
// 	}
// }

/**
 * Compute a comparison period based on the current period and a compareWith strategy.
 * @param {{ start: string, end: string }} currentRange
 * @param {'prior_period'|'prior_week'|'prior_month'} compareWith
 * @returns {{ from: string, to: string }}
 */
export function getComparisonRange(currentRange, compareWith) {
	const startMs = new Date(currentRange.start).getTime()
	const endMs = new Date(currentRange.end).getTime()
	const durationMs = endMs - startMs

	if (compareWith === 'prior_day') {
		const offset = 24 * 60 * 60 * 1000
		return {
			from: new Date(startMs - offset).toISOString(),
			to: new Date(endMs - offset).toISOString()
		}
	}

	if (compareWith === 'prior_week') {
		const offset = 7 * 24 * 60 * 60 * 1000
		return {
			from: new Date(startMs - offset).toISOString(),
			to: new Date(endMs - offset).toISOString()
		}
	}

	if (compareWith === 'prior_month') {
		const s = new Date(currentRange.start)
		s.setMonth(s.getMonth() - 1)
		const e = new Date(s.getTime() + durationMs)
		return { from: s.toISOString(), to: e.toISOString() }
	}

	if (compareWith === 'prior_year') {
		const s = new Date(currentRange.start)
		s.setFullYear(s.getFullYear() - 1)
		const e = new Date(currentRange.end)
		e.setFullYear(e.getFullYear() - 1)
		return { from: s.toISOString(), to: e.toISOString() }
	}

	// prior_period: same duration immediately before
	return {
		from: new Date(startMs - durationMs).toISOString(),
		to: new Date(startMs).toISOString()
	}
}

function sumAddOns(addOns) {
	if (!Array.isArray(addOns)) return 0
	return addOns.reduce((sum, a) => sum + (a.price || 0), 0)
}

const EXCLUDED_STATUSES = '("noShow","cancelled")'

function applyStatusFilter(query, status) {
	if (status === 'all') return query.not('status', 'in', EXCLUDED_STATUSES)
	return query.eq('status', status)
}

function applyEmployeeFilter(query, employeeId) {
	if (employeeId) return query.eq('employee_id', employeeId)
	return query
}

/**
 * Shared fetch: returns all non-cancelled/non-noShow appointments with revenue
 * fields for a given range and optional employee filter. Both getTotalRevenue
 * and getNetSales build on this to avoid duplicate DB calls.
 */
async function fetchRevenueAppointments(merchantId, range, employeeId = null) {
	const { start, end } = resolveRange(range)

	let query = supabase
		.from('appointments')
		.select('start_ts, status, add_ons, service_price, service_types(price)')
		.eq('merchant_id', merchantId)
		.gte('start_ts', start)
		.lte('start_ts', end)
		.not('status', 'in', EXCLUDED_STATUSES)

	query = applyEmployeeFilter(query, employeeId)

	const { data, error } = await query
	if (error) throw error
	return data || []
}

/**
 * Checkout discounts recorded on payments, attributed to the appointment they
 * paid for. Ranged by the appointment's start_ts — the same axis every revenue
 * query uses — so a discount always lands in the period (and chart bucket)
 * whose revenue it reduces, even when checkout happened on a different day.
 * Group payments carry the whole group's discount on the primary appointment's
 * payment row, which is enough for period totals and buckets.
 */
async function fetchDiscounts(merchantId, range, employeeId = null) {
	const { start, end } = resolveRange(range)

	let query = supabase
		.from('payments')
		.select('discount_amount, appointments!inner(start_ts, employee_id, user_id)')
		.eq('merchant_id', merchantId)
		.eq('payment_status', 'completed')
		.gt('discount_amount', 0)
		.gte('appointments.start_ts', start)
		.lte('appointments.start_ts', end)

	if (employeeId) {
		query = query.eq('appointments.employee_id', employeeId)
	}

	const { data, error } = await query
	if (error) throw error
	return data || []
}

function sumDiscounts(discountRows) {
	return discountRows.reduce((sum, p) => sum + (p.discount_amount || 0), 0)
}

/**
 * @param {string} merchantId
 * @param {string|{from,to}} range
 * @param {'all'|'completed'} [status='completed']
 * @param {string|null} [employeeId=null]
 */
export async function getTotalRevenue(merchantId, range, status = 'completed', employeeId = null) {
	const [rows, discountRows] = await Promise.all([
		fetchRevenueAppointments(merchantId, range, employeeId),
		fetchDiscounts(merchantId, range, employeeId)
	])
	const filtered = status === 'completed'
		? rows.filter(a => a.status === 'completed')
		: rows

	const grossCents = filtered.reduce((sum, appt) => {
		return sum + getServicePrice(appt) + sumAddOns(appt.add_ons)
	}, 0)
	// Discounts only exist on completed checkouts, so they net out of both the
	// 'completed' and 'all' views.
	const totalCents = Math.max(0, grossCents - sumDiscounts(discountRows))

	return { totalCents, formatted: `$${(totalCents / 100).toFixed(2)}` }
}

/**
 * Net sales report — single DB call returning two time-series (closed + expected)
 * grouped by day, week, or month. Also returns totals for each series so the
 * caller can skip a separate getTotalRevenue call.
 *
 * @param {string} merchantId
 * @param {string|{from,to}} range
 * @param {'day'|'week'|'month'} [groupBy='day']
 * @param {string|null} [employeeId=null]
 * @returns {{ closed: Array, expected: Array, closedTotalCents: number, closedFormatted: string, expectedTotalCents: number, expectedFormatted: string }}
 */
export async function getNetSales(merchantId, range, groupBy = 'day', employeeId = null) {
	const { start, end } = resolveRange(range)
	const [rows, discountRows] = await Promise.all([
		fetchRevenueAppointments(merchantId, range, employeeId),
		fetchDiscounts(merchantId, range, employeeId)
	])

	const pad = (n) => String(n).padStart(2, '0')

	function getBucketKey(date) {
		if (groupBy === 'month') {
			return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`
		}
		if (groupBy === 'week') {
			const day = date.getDay()
			const diff = day === 0 ? 6 : day - 1
			const mon = new Date(date)
			mon.setDate(mon.getDate() - diff)
			return `${mon.getFullYear()}-${pad(mon.getMonth() + 1)}-${pad(mon.getDate())}`
		}
		return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
	}

	// Generate every period key between start and end so dates with 0 appointments appear
	function generateAllKeys() {
		const keys = []
		const seen = new Set()
		const cursor = new Date(start)
		const endDate = new Date(end)
		while (cursor <= endDate) {
			const key = getBucketKey(cursor)
			if (!seen.has(key)) { seen.add(key); keys.push(key) }
			if (groupBy === 'month') {
				cursor.setMonth(cursor.getMonth() + 1)
			} else if (groupBy === 'week') {
				cursor.setDate(cursor.getDate() + 7)
			} else {
				cursor.setDate(cursor.getDate() + 1)
			}
		}
		return keys
	}

	const closedBuckets = {}
	const expectedBuckets = {}

	for (const appt of rows) {
		const key = getBucketKey(new Date(appt.start_ts))
		const cents = getServicePrice(appt) + sumAddOns(appt.add_ons)

		if (!expectedBuckets[key]) expectedBuckets[key] = 0
		expectedBuckets[key] += cents

		if (appt.status === 'completed') {
			if (!closedBuckets[key]) closedBuckets[key] = 0
			closedBuckets[key] += cents
		}
	}

	// Net checkout discounts out of the bucket the discounted appointment's
	// revenue landed in. Discounts only exist on completed checkouts, so they
	// come out of both series.
	for (const p of discountRows) {
		const startTs = p.appointments?.start_ts
		if (!startTs) continue
		const key = getBucketKey(new Date(startTs))
		expectedBuckets[key] = (expectedBuckets[key] || 0) - (p.discount_amount || 0)
		closedBuckets[key] = (closedBuckets[key] || 0) - (p.discount_amount || 0)
	}

	const allKeys = generateAllKeys()

	const toEntry = (key, cents) => ({
		period: key,
		totalCents: cents,
		formatted: `$${(cents / 100).toFixed(2)}`
	})

	const closedTotalCents = Object.values(closedBuckets).reduce((s, v) => s + v, 0)
	const expectedTotalCents = Object.values(expectedBuckets).reduce((s, v) => s + v, 0)

	return {
		closed: allKeys.map(k => toEntry(k, closedBuckets[k] || 0)),
		expected: allKeys.map(k => toEntry(k, expectedBuckets[k] || 0)),
		closedTotalCents,
		closedFormatted: `$${(closedTotalCents / 100).toFixed(2)}`,
		expectedTotalCents,
		expectedFormatted: `$${(expectedTotalCents / 100).toFixed(2)}`
	}
}

// /**
//  * @param {string} merchantId
//  * @param {string|{from,to}} range
//  * @param {string|null} [employeeId=null]
//  */
// export async function getExpectedRevenue(merchantId, range, employeeId = null) {
// 	if (range === 'yearly') range = 'monthly'
//
// 	const { start, end } = resolveFutureRange(range)
//
// 	let query = supabase
// 		.from('appointments')
// 		.select('add_ons, service_types(price)')
// 		.eq('merchant_id', merchantId)
// 		.eq('status', 'booked')
// 		.gte('start_ts', start)
// 		.lte('start_ts', end)
//
// 	query = applyEmployeeFilter(query, employeeId)
//
// 	const { data, error } = await query
// 	if (error) throw error
//
// 	const totalCents = (data || []).reduce((sum, appt) => {
// 		return sum + (appt.service_types?.price || 0) + sumAddOns(appt.add_ons)
// 	}, 0)
//
// 	return { totalCents, formatted: `$${(totalCents / 100).toFixed(2)}` }
// }

/**
 * @param {string} merchantId
 * @param {string|{from,to}} range
 * @param {'all'|'completed'} [status='completed']
 * @param {string|null} [employeeId=null]
 */
export async function getAppointmentsCount(merchantId, range, status = 'completed', employeeId = null) {
	const { start, end } = resolveRange(range)

	let query = supabase
		.from('appointments')
		.select('id', { count: 'exact', head: true })
		.eq('merchant_id', merchantId)
		.gte('start_ts', start)
		.lte('start_ts', end)

	query = applyStatusFilter(query, status)
	query = applyEmployeeFilter(query, employeeId)

	const { count, error } = await query
	if (error) throw error

	return { count: count || 0 }
}

/**
 * @param {string} merchantId
 * @param {string|{from,to}} range
 * @param {'all'|'completed'} [status='completed']
 * @param {string|null} [employeeId=null]
 */
export async function getAverageTicketValue(merchantId, range, status = 'completed', employeeId = null) {
	const [revenue, appointments] = await Promise.all([
		getTotalRevenue(merchantId, range, status, employeeId),
		getAppointmentsCount(merchantId, range, status, employeeId)
	])

	const averageCents = appointments.count > 0
		? Math.round(revenue.totalCents / appointments.count)
		: 0

	return {
		averageCents,
		formatted: `$${(averageCents / 100).toFixed(2)}`,
		count: appointments.count,
		totalCents: revenue.totalCents
	}
}

/**
 * @param {string} merchantId
 * @param {string|{from,to}} range
 * @param {string|null} [employeeId=null]
 */
export async function getTotalTips(merchantId, range, employeeId = null) {
	const { start, end } = resolveRange(range)

	let query = supabase
		.from('payments')
		.select('tips_amount, appointments!inner(employee_id)')
		.eq('merchant_id', merchantId)
		.eq('payment_status', 'completed')
		.gte('created_at', start)
		.lte('created_at', end)

	if (employeeId) {
		query = query.eq('appointments.employee_id', employeeId)
	}

	const { data, error } = await query
	if (error) throw error

	const totalCents = (data || []).reduce((sum, p) => sum + (p.tips_amount || 0), 0)

	return { totalCents, formatted: `$${(totalCents / 100).toFixed(2)}` }
}

/**
 * @param {string} merchantId
 * @param {string|{from,to}} range
 */
export async function getTotalTaxes(merchantId, range) {
	const { start, end } = resolveRange(range)

	const { data, error } = await supabase
		.from('payments')
		.select('tax_amount')
		.eq('merchant_id', merchantId)
		.eq('payment_status', 'completed')
		.gte('created_at', start)
		.lte('created_at', end)

	if (error) throw error

	const totalCents = (data || []).reduce((sum, p) => sum + (p.tax_amount || 0), 0)

	return { totalCents, formatted: `$${(totalCents / 100).toFixed(2)}` }
}

/**
 * Services summary — single DB call returning service/add-on counts for the
 * donut chart and top 5 services by revenue for the list.
 *
 * @param {string} merchantId
 * @param {string|{from,to}} range
 * @param {'all'|'completed'} [status='completed']
 * @param {string|null} [employeeId=null]
 */
export async function getServicesSummary(merchantId, range, status = 'completed', employeeId = null) {
	const { start, end } = resolveRange(range)

	let query = supabase
		.from('appointments')
		.select('add_ons, service_price, service_types!inner(price, name)')
		.eq('merchant_id', merchantId)
		.gte('start_ts', start)
		.lte('start_ts', end)

	query = applyStatusFilter(query, status)
	query = applyEmployeeFilter(query, employeeId)

	const { data, error } = await query
	if (error) throw error

	const rows = data || []
	let serviceCount = rows.length
	let addOnCount = 0
	const serviceBuckets = {}

	for (const appt of rows) {
		const name = appt.service_types?.name || 'Unknown'
		const price = getServicePrice(appt)
		const addOnsTotal = sumAddOns(appt.add_ons)

		if (!serviceBuckets[name]) serviceBuckets[name] = { revenueCents: 0, volume: 0 }
		serviceBuckets[name].revenueCents += price + addOnsTotal
		serviceBuckets[name].volume += 1

		if (Array.isArray(appt.add_ons)) addOnCount += appt.add_ons.length
	}

	const topServices = Object.entries(serviceBuckets)
		.map(([name, stats]) => ({
			name,
			revenueCents: stats.revenueCents,
			revenueFormatted: `$${(stats.revenueCents / 100).toFixed(2)}`,
			volume: stats.volume
		}))
		.sort((a, b) => b.revenueCents - a.revenueCents)
		.slice(0, 5)

	return { serviceCount, addOnCount, topServices }
}

/**
 * @param {string} merchantId
 * @param {string|{from,to}} range
 * @param {Object} options
 */
export async function getTopServices(merchantId, range, { groupBy, sortBy, limit = 5, status = 'completed', employeeId = null }) {
	const { start, end } = resolveRange(range)

	const selectFields = groupBy === 'categories'
		? 'add_ons, service_price, service_types!inner(price, name, categories(name))'
		: 'add_ons, service_price, service_types!inner(price, name, is_service, is_add_on, category_id, categories(name))'

	let query = supabase
		.from('appointments')
		.select(selectFields)
		.eq('merchant_id', merchantId)
		.gte('start_ts', start)
		.lte('start_ts', end)

	query = applyStatusFilter(query, status)
	query = applyEmployeeFilter(query, employeeId)

	const { data, error } = await query
	if (error) throw error

	const rows = data || []
	const buckets = {}

	if (groupBy === 'addons') {
		for (const appt of rows) {
			if (!Array.isArray(appt.add_ons)) continue
			for (const addon of appt.add_ons) {
				const key = addon.name || 'Unknown'
				if (!buckets[key]) buckets[key] = { revenueCents: 0, volume: 0 }
				buckets[key].revenueCents += addon.price || 0
				buckets[key].volume += 1
			}
		}
	} else if (groupBy === 'categories') {
		for (const appt of rows) {
			const key = appt.service_types?.categories?.name || 'Uncategorized'
			if (!buckets[key]) buckets[key] = { revenueCents: 0, volume: 0 }
			buckets[key].revenueCents += getServicePrice(appt)
			buckets[key].revenueCents += sumAddOns(appt.add_ons)
			buckets[key].volume += 1
		}
	} else {
		for (const appt of rows) {
			const key = appt.service_types?.name || 'Unknown'
			if (!buckets[key]) buckets[key] = { revenueCents: 0, volume: 0 }
			buckets[key].revenueCents += getServicePrice(appt)
			buckets[key].revenueCents += sumAddOns(appt.add_ons)
			buckets[key].volume += 1
		}
	}

	return Object.entries(buckets)
		.map(([name, stats]) => ({
			name,
			revenueCents: stats.revenueCents,
			revenueFormatted: `$${(stats.revenueCents / 100).toFixed(2)}`,
			volume: stats.volume
		}))
		.sort((a, b) => sortBy === 'revenue' ? b.revenueCents - a.revenueCents : b.volume - a.volume)
		.slice(0, limit)
}

/**
 * @param {string} merchantId
 * @param {string|{from,to}} range
 * @param {'all'|'completed'} [status='completed']
 * @param {string|null} [employeeId=null]
 */
export async function getClientsPerformance(merchantId, range, status = 'completed', employeeId = null) {
	const { start, end } = resolveRange(range)

	let periodQuery = supabase
		.from('appointments')
		.select('id, user_id, status, add_ons, service_price, service_types(price)')
		.eq('merchant_id', merchantId)
		.gte('start_ts', start)
		.lte('start_ts', end)

	if (employeeId) periodQuery = periodQuery.eq('employee_id', employeeId)

	let pastQuery = supabase
		.from('appointments')
		.select('user_id')
		.eq('merchant_id', merchantId)
		.lt('start_ts', start)
		.neq('status', 'cancelled')

	if (employeeId) pastQuery = pastQuery.eq('employee_id', employeeId)

	let futureQuery = supabase
		.from('appointments')
		.select('user_id')
		.eq('merchant_id', merchantId)
		.gt('start_ts', end)
		.neq('status', 'cancelled')

	if (employeeId) futureQuery = futureQuery.eq('employee_id', employeeId)

	const [periodRes, pastRes, futureRes, paymentsRes, discountRows] = await Promise.all([
		periodQuery,
		pastQuery,
		futureQuery,
		supabase
			.from('payments')
			.select('tips_amount')
			.eq('merchant_id', merchantId)
			.eq('payment_status', 'completed')
			.gte('created_at', start)
			.lte('created_at', end),
		fetchDiscounts(merchantId, range, employeeId)
	])

	if (periodRes.error) throw periodRes.error
	if (pastRes.error) throw pastRes.error
	if (futureRes.error) throw futureRes.error
	if (paymentsRes.error) throw paymentsRes.error

	const periodAppts = periodRes.data || []
	const pastClientIds = new Set((pastRes.data || []).map(a => a.user_id).filter(Boolean))
	const futureClientIds = new Set((futureRes.data || []).map(a => a.user_id).filter(Boolean))

	const activeAppts = status === 'completed'
		? periodAppts.filter(a => a.status === 'completed' && a.user_id)
		: periodAppts.filter(a => a.status !== 'cancelled' && a.status !== 'noShow' && a.user_id)

	const clientApptCounts = {}
	for (const appt of activeAppts) {
		clientApptCounts[appt.user_id] = (clientApptCounts[appt.user_id] || 0) + 1
	}

	const clientIds = Object.keys(clientApptCounts)
	const clientsServed = clientIds.length

	let newClients = 0
	let returningClients = 0
	for (const clientId of clientIds) {
		if (clientApptCounts[clientId] >= 2 || pastClientIds.has(clientId)) {
			returningClients++
		} else {
			newClients++
		}
	}

	let rebookClients = 0
	for (const clientId of clientIds) {
		if (clientApptCounts[clientId] >= 2 || futureClientIds.has(clientId)) {
			rebookClients++
		}
	}
	const rebookRate = clientsServed > 0
		? Math.round((rebookClients / clientsServed) * 10000) / 100
		: 0

	const revenueAppts = status === 'completed'
		? periodAppts.filter(a => a.status === 'completed' && a.user_id)
		: periodAppts.filter(a => a.status !== 'noShow' && a.status !== 'cancelled' && a.user_id)
	// Revenue here only counts appointments with a client, so only net out
	// discounts from client appointments too.
	const clientDiscountCents = sumDiscounts(discountRows.filter(p => p.appointments?.user_id))
	const totalRevenueCents = Math.max(0, revenueAppts.reduce((sum, appt) => {
		return sum + getServicePrice(appt) + sumAddOns(appt.add_ons)
	}, 0) - clientDiscountCents)
	const averageRevenuePerClientCents = clientsServed > 0
		? Math.round(totalRevenueCents / clientsServed)
		: 0

	const totalAppts = periodAppts.filter(a => a.user_id).length
	const cancelledCount = periodAppts.filter(a => a.status === 'cancelled' && a.user_id).length
	const noShowCount = periodAppts.filter(a => a.status === 'noShow' && a.user_id).length
	const lateCancellationRate = totalAppts > 0
		? Math.round((cancelledCount / totalAppts) * 10000) / 100
		: 0
	const noShowRate = totalAppts > 0
		? Math.round((noShowCount / totalAppts) * 10000) / 100
		: 0

	const totalTipsCents = (paymentsRes.data || []).reduce((sum, p) => sum + (p.tips_amount || 0), 0)
	const averageTipRate = totalRevenueCents > 0
		? Math.round((totalTipsCents / totalRevenueCents) * 10000) / 100
		: 0

	return {
		clientsServed,
		newClients,
		returningClients,
		rebookRate,
		averageRevenuePerClientCents,
		averageRevenuePerClientFormatted: `$${(averageRevenuePerClientCents / 100).toFixed(2)}`,
		lateCancellationRate,
		noShowRate,
		averageTipRate
	}
}

/**
 * Pets summary — single DB call returning species counts for the donut chart
 * and top 5 breeds by revenue for the list.
 *
 * @param {string} merchantId
 * @param {string|{from,to}} range
 * @param {'all'|'completed'} [status='completed']
 * @param {string|null} [employeeId=null]
 */
export async function getPetsSummary(merchantId, range, status = 'completed', employeeId = null) {
	const { start, end } = resolveRange(range)

	let query = supabase
		.from('appointments')
		.select('add_ons, service_price, service_types(price), pets!pet_id(breed, species)')
		.eq('merchant_id', merchantId)
		.gte('start_ts', start)
		.lte('start_ts', end)

	query = applyStatusFilter(query, status)
	query = applyEmployeeFilter(query, employeeId)

	const { data, error } = await query
	if (error) throw error

	const speciesBuckets = {}
	const breedBuckets = {}

	for (const appt of data || []) {
		const pet = appt.pets
		if (!pet) continue
		const species = pet.species || 'Unknown'
		const breed = pet.breed || 'Unknown'
		const cents = getServicePrice(appt) + sumAddOns(appt.add_ons)

		if (!speciesBuckets[species]) speciesBuckets[species] = 0
		speciesBuckets[species] += 1

		if (!breedBuckets[breed]) breedBuckets[breed] = { revenueCents: 0, volume: 0 }
		breedBuckets[breed].revenueCents += cents
		breedBuckets[breed].volume += 1
	}

	const speciesCounts = Object.entries(speciesBuckets)
		.map(([name, count]) => ({ name, count }))
		.sort((a, b) => b.count - a.count)

	const totalPets = speciesCounts.reduce((s, v) => s + v.count, 0)

	const topBreeds = Object.entries(breedBuckets)
		.map(([name, stats]) => ({
			name,
			revenueCents: stats.revenueCents,
			revenueFormatted: `$${(stats.revenueCents / 100).toFixed(2)}`,
			volume: stats.volume
		}))
		.sort((a, b) => b.revenueCents - a.revenueCents)
		.slice(0, 5)

	return { speciesCounts, totalPets, topBreeds }
}

/**
 * @param {string} merchantId
 * @param {string|{from,to}} range
 * @param {Object} options
 */
export async function getTopPetBreeds(merchantId, range, { sortBy, limit = 5, status = 'completed', employeeId = null }) {
	const { start, end } = resolveRange(range)

	let query = supabase
		.from('appointments')
		.select('add_ons, service_price, service_types(price), pets!pet_id(breed)')
		.eq('merchant_id', merchantId)
		.gte('start_ts', start)
		.lte('start_ts', end)

	query = applyStatusFilter(query, status)
	query = applyEmployeeFilter(query, employeeId)

	const { data, error } = await query
	if (error) throw error

	const buckets = {}
	for (const appt of data || []) {
		const pet = appt.pets
		if (!pet) continue
		const breed = pet.breed || 'Unknown'
		if (!buckets[breed]) buckets[breed] = { revenueCents: 0, volume: 0 }
		buckets[breed].revenueCents += getServicePrice(appt)
		buckets[breed].revenueCents += sumAddOns(appt.add_ons)
		buckets[breed].volume += 1
	}

	return Object.entries(buckets)
		.map(([name, stats]) => ({
			name,
			revenueCents: stats.revenueCents,
			revenueFormatted: `$${(stats.revenueCents / 100).toFixed(2)}`,
			volume: stats.volume
		}))
		.sort((a, b) => sortBy === 'revenue' ? b.revenueCents - a.revenueCents : b.volume - a.volume)
		.slice(0, limit)
}

/**
 * @param {string} merchantId
 * @param {string|{from,to}} range
 * @param {'all'|'completed'} [status='completed']
 * @param {string|null} [employeeId=null]
 */
export async function getTotalPetsServed(merchantId, range, status = 'completed', employeeId = null) {
	const { start, end } = resolveRange(range)

	let query = supabase
		.from('appointments')
		.select('pet_id')
		.eq('merchant_id', merchantId)
		.gte('start_ts', start)
		.lte('start_ts', end)
		.not('pet_id', 'is', null)

	query = applyStatusFilter(query, status)
	query = applyEmployeeFilter(query, employeeId)

	const { data, error } = await query
	if (error) throw error

	const uniquePetIds = new Set((data || []).map(a => a.pet_id))
	return { count: uniquePetIds.size }
}

/**
 * Today's appointments — fetches all appointments for today (non-grouped or group primary)
 * with client name, pet name, status, service name, and date/time.
 *
 * @param {string} merchantId
 * @param {string|null} [employeeId=null]
 * @returns {Promise<Array<{id, clientName, petName, petId, status, serviceName, startTs, endTs, userId, appointmentData}>>}
 */
export async function getTodaysAppointments(merchantId, employeeId = null) {
	const now = new Date()
	const pad = (n) => String(n).padStart(2, '0')
	const todayStart = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T00:00:00`
	const todayEnd = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T23:59:59`

	let query = supabase
		.from('appointments')
		.select('id, user_id, pet_id, merchant_id, service_id, employee_id, status, start_ts, end_ts, customer_name, customer_email, phone_number, comments, group_id, is_group_primary, add_ons, agreements, agreements_signed, service_price, service_types(id, name, price, duration_minutes, appointment_color), pets(id, name, species), clients(id, name, last_name)')
		.eq('merchant_id', merchantId)
		.gte('start_ts', new Date(todayStart).toISOString())
		.lte('start_ts', new Date(todayEnd).toISOString())
		.order('start_ts', { ascending: true })

	query = applyEmployeeFilter(query, employeeId)

	const { data, error } = await query
	if (error) throw error

	return (data || [])
		.filter(a => !a.group_id || a.is_group_primary)
		.map(a => ({
			id: a.id,
			clientName: a.clients
				? `${a.clients.name || ''} ${a.clients.last_name || ''}`.trim()
				: a.customer_name || 'Walk-in',
			petName: a.pets?.name || '—',
			petId: a.pet_id,
			status: a.status,
			serviceName: a.service_types?.name || '—',
			startTs: a.start_ts,
			endTs: a.end_ts,
			userId: a.user_id,
			appointmentData: a
		}))
}

/**
 * Compute percentage change between current and prior values.
 * @returns {number|null} percentage change (e.g. 12.5 for +12.5%), null if prior is 0
 */
export function computeChange(current, prior) {
	if (prior === 0) return current > 0 ? 100 : null
	return Math.round(((current - prior) / prior) * 10000) / 100
}
